'use client';
import { useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import {
  useVerifyPaymentStatusMutation,
  useConfirmChallengeMutation,
} from '@/store/checkout/checkout.api';
import { useSaveBookingMutation } from '@/store/restore-booking/restoreBooking.api';
import useCallApiProcess from '../useCallApiProcess';
import useStepper from './useStepper';
import { usePayment, useBooking, useEvent } from '@/store';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import useUserTracking from '../userTracking';
import useStore from '@/store/hooks/useStore';

const MAX_RETRIES = 120;

export default function useCheckoutPayment() {
  const { t } = useTranslation();
  const router = useRouter();
  const { getStoreCopy } = useStore();
  const { jumpTo } = useStepper();

  const [confirmChallengeMut] = useConfirmChallengeMutation();
  const [saveBookingInfo] = useSaveBookingMutation();
  const [verifyPaymentStatusRequest] = useVerifyPaymentStatusMutation();

  const eventData = useSelector((state) => state.event);

  const summary = useSelector((state) => state.summary, shallowEqual);
  const ticketInsurance = useSelector((state) => state.addons.ticketInsurance);

  const { changeIsInChallengeStatus, attemptsCount, setPaymentStatus } =
    usePayment();
  const {
    booking,
    booking: { order },
  } = useBooking();
  const {
    event: { gateway },
  } = useEvent();

  const { purchase, reserveTickets } = useAnalyticsEvents();

  const {
    purchase: purchaseTrackingEvent,
    flowStarted,
    abort,
    redirectInitiated,
    challengeFailed,
    challengeSuccess,
  } = useUserTracking();

  const { closeModal, confirmBackCode, confirm } = useCallApiProcess(
    null,
    t('checkout.loadings.processingPayment'),
  );

  const sendPuchaseEvent = (oxxo = false) => {
    if (oxxo) {
      reserveTickets({
        event: eventData?.event?.id,
        currency: eventData.preferences.currency,
        value: summary.total_to_pay,
      });
    } else {
      purchase({
        event: eventData?.event?.id,
        currency: eventData.preferences.currency,
        value: summary.total_to_pay,
        total: summary.total_to_pay,
        ticket_insurance: ticketInsurance.selected,
      });
    }
  };

  const verifyPaymentStatus = async () => {
    try {
      const response = await verifyPaymentStatusRequest(
        router.query?.order || booking.order,
      );
      return response;
    } catch {
      return false;
    }
  };

  const waitAndCheckStatus = () =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        verifyPaymentStatus()
          .then((resp) => {
            resolve(resp?.data);
          })
          .catch(() => {
            reject({ error: 'Error en el servidor' });
          });
      }, 1000);
    });

  const checkPaymentStatus = async (skipUrl = false) => {
    let status = 'paying-gateway';
    let url = '';
    let counter = 0;

    do {
      counter++;
      if (counter >= MAX_RETRIES) {
        status = 'notCompleted';
        break;
      }

      const resp = await waitAndCheckStatus();

      if (!resp) {
        continue;
      }

      if (resp.status === 'failed' || resp.status === 'paying') {
        status = 'notCompleted';
        closeModal();
      }

      if (resp.status === 'complete') {
        status = 'complete';
        closeModal();
      }

      if (resp.payer_authentication_url && !skipUrl) {
        status = 'need3DsChallenge';
        changeIsInChallengeStatus(true);
        url = resp.payer_authentication_url;
      }
    } while (status === 'paying-gateway' && !url);

    return {
      status,
      url,
    };
  };

  const handleNotCompleted = (type, challengeStatus) => {
    if (type === 'onConfirm') {
      confirm({
        title: 'Verificación de seguridad no completada',
        text: 'No se pudo realizar la verificación de seguridad con tu banco. Para finalizar tu compra, inténtalo nuevamente o utiliza otro método de pago.',
        acceptLabel: 'Entendido',
        omitBooking: true,
      });
      challengeFailed({
        reason:
          challengeStatus === 'aborting'
            ? 'user_cancelled'
            : 'authentication_failed',
      });
      jumpTo('PAYMENT');
    }
    if (type === 'onPayment') {
      if (gateway === 'ADYEN_PAY') window.Spreedly?.removeHandlers();
      closeModal();
      confirmBackCode('00012', null);
    }
  };

  const onAbort = async (abortByFailure) => {
    changeIsInChallengeStatus(false);
    await confirmChallengeMut({
      challengeStatus: 'aborting',
      id: booking.order,
      gateway,
    });
    closeModal();

    if (abortByFailure) {
      handleNotCompleted('onConfirm', 'aborting');
    }
  };

  const handleFailSaveBooking = ({ error, message }) => {
    abort({
      error,
      message,
    });
    onAbort();
    closeModal();
    setPaymentStatus(null);
  };

  const verifyPaymentStatusResponse = async (
    payment,
    type,
    challengeStatus,
  ) => {
    if (payment.status === 'need3DsChallenge') {
      try {
        flowStarted();

        const result = await saveBookingInfo({
          booking_number: order,
          local_storage: getStoreCopy(),
        });

        if (!result?.error) {
          redirectInitiated();
          setPaymentStatus(null);
          const params = new URLSearchParams({ url: payment.url });
          window.location.href = `/api/3ds-redirect?${params}`;
          return;
        }
        handleFailSaveBooking({
          error: result?.error,
          message: 'Fallo en la respuesta de guardar bookingInfo',
        });
      } catch (error) {
        handleFailSaveBooking({
          error,
          message: 'Fallo en la llamada a la función de guardar bookingIfo',
        });
      }
    }

    if (payment.status === 'notCompleted') {
      handleNotCompleted(type, challengeStatus);
    }

    if (payment.status === 'complete') {
      if (type === 'onConfirm') {
        challengeSuccess();
      }
      sendPuchaseEvent();
      purchaseTrackingEvent({ type: 'paid', method: 'card' });
      setPaymentStatus(null);
      jumpTo('SUCCESS');
    }
  };

  return {
    waitAndCheckStatus,
    checkPaymentStatus,
    verifyPaymentStatusResponse,
    confirmChallengeMut,
    paymentAttemptsCount: attemptsCount,
  };
}
